import {IMAGE_MIME_TYPE} from '@mantine/dropzone';
import {produce} from 'immer';
import React from 'react';
import {FormField} from 'src/app/forms/FormField';
import type {IFormFieldV2} from 'src/types/FormV2';
import {
  useGetSelectedField,
  useUpdateSelectedField,
} from '../../../hooks/useFormBuilder';

// Define the settings fields using IFormFieldV2
const fileTypeField: IFormFieldV2 = {
  name: 'allowedFileTypes',
  label: 'Allowed File Types',
  type: 'select',
  required: true,
  options: [
    {id: 'any', label: 'Any File Type'},
    {id: 'images', label: 'Images Only'},
  ],
};

const allowMultipleField: IFormFieldV2 = {
  name: 'allowMultiple',
  label: 'Allow Multiple Uploads',
  type: 'switch',
  description: 'Allow users to upload multiple files at once',
};

export const FileUploadSettings: React.FC = () => {
  const field = useGetSelectedField();
  const updateSelectedField = useUpdateSelectedField();

  const handleFileTypeChange = React.useCallback(
    (name: string, value: string) => {
      if (!field) return;
      updateSelectedField(
        produce(field, draft => {
          if (!draft.fileUploadOptions) {
            draft.fileUploadOptions = {};
          }

          if (value === 'images') {
            draft.fileUploadOptions.allowedFileTypes = IMAGE_MIME_TYPE;
            draft.fileUploadOptions.renderUploadsAsImage = true;
          } else {
            draft.fileUploadOptions.allowedFileTypes = undefined;
            draft.fileUploadOptions.renderUploadsAsImage = false;
          }
        })
      );
    },
    [updateSelectedField, field]
  );

  const handleAllowMultipleChange = React.useCallback(
    (name: string, value: boolean) => {
      if (!field) return;
      updateSelectedField(
        produce(field, draft => {
          if (!draft.fileUploadOptions) {
            draft.fileUploadOptions = {};
          }
          draft.fileUploadOptions.allowMultiple = value;
        })
      );
    },
    [updateSelectedField, field]
  );

  const currentFileType = React.useMemo(
    () =>
      field?.fileUploadOptions?.allowedFileTypes?.includes('image/png')
        ? 'images'
        : 'any',
    [field?.fileUploadOptions]
  );

  if (!field || field.type !== 'file') return null;

  return (
    <>
      <FormField
        field={fileTypeField}
        value={currentFileType}
        onChange={handleFileTypeChange}
      />
      <FormField
        field={allowMultipleField}
        value={field.fileUploadOptions?.allowMultiple ?? false}
        onChange={handleAllowMultipleChange}
      />
    </>
  );
};
